import { ChartConfiguration, ChartData, ChartDataset, ChartType, Plugin } from 'chart.js';

/**
 * Translates the ApexCharts option objects this app has always built into the
 * Chart.js config that <canvas baseChart> wants.
 *
 * WHY AN ADAPTER RATHER THAN 10 REWRITES
 * --------------------------------------
 * ApexCharts had to go: apexcharts 5.x - the only line the Angular 21 wrapper
 * accepts - is dual-licensed and stops being free above $2M annual revenue.
 * chart.js + ng2-charts are MIT with no such threshold and were already
 * dependencies here.
 *
 * The 10 charts are described by plain option objects (series, xaxis, stroke,
 * fill, colors...), and 9 of them are the same stacked-bar shape with different
 * data. Converting the description in one place, instead of hand-porting each
 * call site, keeps the components' data assembly untouched - so the only thing
 * that can regress is this file, and it is the only thing to review.
 *
 * Same approach the DevExtreme grid removal took (see data-grid.adapter.ts).
 */

/** ApexCharts' default palette, so multi-series colours land where they used to. */
const APEX_PALETTE = [
  '#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0',
  '#3F51B5', '#03A9F4', '#4CAF50', '#F9CE1D', '#FF9800'
];

export interface ChartSpec {
  type: ChartType;
  data: ChartData;
  options: ChartConfiguration['options'];
  plugins: Plugin[];
  /** `chart.height` - the host div is sized in px, the canvas fills it. */
  height: number;
  /** `chart.width`, when the original pinned one (the custom-grid cards do). */
  width: number | null;
}

/** '#544795' + 0.3 -> 'rgba(84,71,149,0.3)'. Passes through non-hex untouched. */
function withAlpha(color: string, alpha: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(color || '');
  if (!m || alpha >= 1) { return color; }
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

/** `title` is sometimes a string (custom-grid) and sometimes { text } (the rest). */
function titleText(title: any): string {
  if (!title) { return ''; }
  return typeof title === 'string' ? title : (title.text || '');
}

function legendPosition(pos: any): 'top' | 'bottom' | 'left' | 'right' {
  return pos === 'top' || pos === 'left' || pos === 'right' ? pos : 'bottom';
}

/**
 * Draws the series value on each element, which is what `dataLabels.enabled`
 * did in ApexCharts. Written inline rather than pulling in
 * chartjs-plugin-datalabels - one less dependency to carry to Angular 22.
 */
const valueLabelsPlugin: Plugin = {
  id: 'apexLikeValueLabels',
  afterDatasetsDraw(chart) {
    const ctx = chart.ctx;
    ctx.save();
    ctx.font = '11px Helvetica, Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    chart.data.datasets.forEach((dataset: any, i) => {
      const meta = chart.getDatasetMeta(i);
      if (meta.hidden) { return; }
      meta.data.forEach((element: any, j) => {
        const raw = dataset.data[j];
        const value = typeof raw === 'number' ? raw : Number(raw);
        if (!value) { return; }               // Apex hides zero/empty labels too
        const isBar = (meta.type || dataset.type) === 'bar';
        ctx.fillStyle = isBar ? '#ffffff' : '#373d3f';
        ctx.fillText(String(raw), element.x, isBar ? element.y + 10 : element.y - 8);
      });
    });
    ctx.restore();
  }
};

export function toChartSpec(opt: any): ChartSpec | null {
  if (!opt) { return null; }

  const chart = opt.chart || {};
  const apexType: string = chart.type || 'line';
  const sparkline = !!(chart.sparkline && chart.sparkline.enabled);
  const stacked = !!chart.stacked;
  const stroke = opt.stroke || {};
  const fill = opt.fill || {};
  const colors: string[] = Array.isArray(opt.colors) ? opt.colors : [];
  const showLegend = sparkline ? false : (opt.legend ? opt.legend.show !== false : true);

  // yaxis is an object for single-axis charts and an array for the dual-axis
  // (column + line) ones. Normalise to an array and remember which it was.
  const yAxes: any[] = Array.isArray(opt.yaxis) ? opt.yaxis : [opt.yaxis || {}];
  const dualAxis = Array.isArray(opt.yaxis) && opt.yaxis.length > 1;

  // ---- pie / donut: series is a flat number[] and labels carry the slices ----
  if (apexType === 'pie' || apexType === 'donut') {
    const values: number[] = Array.isArray(opt.series) ? opt.series : [];
    const data: ChartData = {
      labels: opt.labels || [],
      datasets: [{
        data: values,
        backgroundColor: values.map((_, i) => colors[i] || APEX_PALETTE[i % APEX_PALETTE.length]),
        borderColor: '#ffffff',
        borderWidth: 1
      } as ChartDataset]
    };
    return {
      type: apexType === 'donut' ? 'doughnut' : 'pie',
      data,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: showLegend, position: legendPosition(opt.legend && opt.legend.position) },
          title: { display: !!titleText(opt.title), text: titleText(opt.title) },
          tooltip: { enabled: true }
        }
      },
      plugins: opt.dataLabels && opt.dataLabels.enabled ? [valueLabelsPlugin] : [],
      height: chart.height || 320,
      width: chart.width || null
    };
  }

  // ---- axis charts: bar / line / area, optionally mixed and dual-axis ----
  const series: any[] = Array.isArray(opt.series) ? opt.series : [];
  const strokeWidths = Array.isArray(stroke.width) ? stroke.width : null;

  const datasets: ChartDataset[] = series.map((s, i) => {
    const seriesType = (s && s.type) || '';
    const isBar = seriesType === 'column' || seriesType === 'bar' ||
                  (!seriesType && apexType === 'bar');
    const isArea = !isBar && (seriesType === 'area' || (!seriesType && apexType === 'area'));
    const color = (s && s.color) || colors[i] || APEX_PALETTE[i % APEX_PALETTE.length];
    const width = strokeWidths ? strokeWidths[i] : stroke.width;
    const areaOpacity = typeof fill.opacity === 'number' ? fill.opacity : 0.3;

    return {
      type: isBar ? 'bar' : 'line',
      label: (s && s.name) || '',
      data: (s && s.data) || [],
      borderColor: color,
      backgroundColor: isBar ? color : (isArea ? withAlpha(color, areaOpacity) : color),
      fill: isArea,
      borderWidth: typeof width === 'number' ? width : (isBar ? 0 : 2),
      tension: stroke.curve === 'smooth' ? 0.4 : 0,
      stepped: stroke.curve === 'stepline',
      pointRadius: sparkline ? 0 : (opt.markers && typeof opt.markers.size === 'number' ? opt.markers.size : 2),
      yAxisID: dualAxis && i > 0 ? 'y1' : 'y'
    } as ChartDataset;
  });

  // A mixed chart has to be rooted at 'bar' for Chart.js to lay the bars out.
  const rootType: ChartType = datasets.some(d => (d as any).type === 'bar') ? 'bar' : 'line';

  const xTitle = opt.xaxis && opt.xaxis.title ? titleText(opt.xaxis.title) : '';
  const yTitle = titleText(yAxes[0] && yAxes[0].title);
  const yFormatter = yAxes[0] && yAxes[0].labels && yAxes[0].labels.formatter;

  const scales: any = {
    x: {
      display: !sparkline,
      stacked,
      title: { display: !!xTitle, text: xTitle },
      grid: { display: !sparkline }
    },
    y: {
      display: !sparkline,
      stacked,
      min: yAxes[0] ? yAxes[0].min : undefined,
      title: { display: !!yTitle, text: yTitle },
      grid: { display: !sparkline },
      ticks: yFormatter ? { callback: (v: any) => yFormatter(Number(v)) } : {}
    }
  };

  if (dualAxis) {
    const right = yAxes[1] || {};
    const rightTitle = titleText(right.title);
    scales.y1 = {
      display: !sparkline,
      position: right.opposite ? 'right' : 'left',
      title: { display: !!rightTitle, text: rightTitle },
      // Only the left axis draws gridlines, matching Apex's opposite-axis look.
      grid: { drawOnChartArea: false }
    };
  }

  return {
    type: rootType,
    data: {
      labels: (opt.xaxis && opt.xaxis.categories) || opt.labels || [],
      datasets
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      layout: sparkline ? { padding: 0 } : {},
      scales,
      plugins: {
        legend: { display: showLegend, position: legendPosition(opt.legend && opt.legend.position) },
        title: { display: !!titleText(opt.title), text: titleText(opt.title) },
        tooltip: { enabled: true }
      }
    },
    plugins: opt.dataLabels && opt.dataLabels.enabled ? [valueLabelsPlugin] : [],
    height: chart.height || 350,
    width: chart.width || null
  };
}
